import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useT } from "../i18n";
import { ancestorsOf, type NavNode } from "../nav/menu";
import { Icon } from "./Icon";

type FlatItem = { node: NavNode; level: number; parentId: string | null; hasChildren: boolean };

function flatten(nodes: NavNode[], expanded: Set<string>, level = 1, parentId: string | null = null, out: FlatItem[] = []) {
  for (const node of nodes) {
    const hasChildren = !!node.children && node.children.length > 0;
    out.push({ node, level, parentId, hasChildren });
    if (hasChildren && expanded.has(node.id)) flatten(node.children!, expanded, level + 1, node.id, out);
  }
  return out;
}

function countVehicles(n: NavNode): number {
  if (n.kind === "vehicle") return 1;
  return (n.children ?? []).reduce((acc, c) => acc + countVehicles(c), 0);
}

export const DEFAULT_EXPANDED = ["g:monitoring", "g:fleet", "g:operators", "g:reports", "g:admin", "sites"];

/** localStorage key of the expanded menu nodes, per signed-in user (UI phase B, ADR 0029). */
export const navStorageKey = (userId: string) => `fts.nav.expanded.${userId}`;

/** Stored expanded ids, or null when nothing (valid) is stored or storage is unavailable. */
export function readExpanded(key: string | undefined): string[] | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) && v.every((x) => typeof x === "string") ? (v as string[]) : null;
  } catch {
    return null;
  }
}

function writeExpanded(key: string | undefined, ids: Set<string>): void {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // Private mode or full storage: the menu simply opens with the defaults next time.
  }
}

export function TreeMenu({
  nodes,
  activeHref,
  onNavigate,
  storageKey,
}: {
  nodes: NavNode[];
  activeHref: string;
  onNavigate: (href: string) => void;
  /** Remember which groups are open (e.g. navStorageKey(user.id)); omitted = defaults every time. */
  storageKey?: string;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(readExpanded(storageKey) ?? DEFAULT_EXPANDED));
  const refs = useRef(new Map<string, HTMLLIElement>());

  // Another user signs in on this browser: load their own menu state.
  const lastKey = useRef(storageKey);
  useEffect(() => {
    if (lastKey.current === storageKey) return;
    lastKey.current = storageKey;
    setExpanded(new Set(readExpanded(storageKey) ?? DEFAULT_EXPANDED));
  }, [storageKey]);

  useEffect(() => writeExpanded(storageKey, expanded), [storageKey, expanded]);
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // Reveal the active node whenever the route changes.
  useEffect(() => {
    const anc = ancestorsOf(nodes, activeHref);
    // An opened site also shows its own units and vehicles.
    if (anc && activeHref.startsWith("#/sites/")) anc.push(`site:${decodeURIComponent(activeHref.slice("#/sites/".length))}`);
    if (anc && anc.some((id) => !expanded.has(id))) {
      setExpanded((prev) => new Set([...prev, ...anc]));
    }
    // Intentionally not depending on `expanded`: user collapses must stick until the route changes.
  }, [activeHref, nodes]);

  const flat = useMemo(() => flatten(nodes, expanded), [nodes, expanded]);
  const activeId = flat.find((f) => f.node.href === activeHref)?.node.id ?? null;
  // A collapsed group that holds the current page keeps a marker, so the active state never disappears.
  const activeAncestors = useMemo(() => new Set(ancestorsOf(nodes, activeHref) ?? []), [nodes, activeHref]);

  // Keep the current page visible in a long rail (many sites and vehicles).
  useEffect(() => {
    if (!activeId) return;
    const row = refs.current.get(activeId)?.querySelector(".tree-row");
    if (row && typeof (row as HTMLElement).scrollIntoView === "function") (row as HTMLElement).scrollIntoView({ block: "nearest" });
  }, [activeId]);
  const tabbableId = flat.some((f) => f.node.id === focusedId) ? focusedId : (activeId ?? flat[0]?.node.id ?? null);

  const focus = useCallback((id: string | undefined | null) => {
    if (!id) return;
    setFocusedId(id);
    refs.current.get(id)?.focus();
  }, []);

  const toggle = (id: string, open?: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      const shouldOpen = open ?? !next.has(id);
      if (shouldOpen) next.add(id);
      else next.delete(id);
      return next;
    });

  const activate = (item: FlatItem) => {
    if (item.node.href) {
      if (item.hasChildren && !expanded.has(item.node.id)) toggle(item.node.id, true);
      onNavigate(item.node.href);
    } else if (item.hasChildren) toggle(item.node.id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const idx = flat.findIndex((f) => f.node.id === tabbableId);
    const cur = flat[idx];
    if (!cur) return;
    const isOpen = expanded.has(cur.node.id);
    let handled = true;
    switch (e.key) {
      case "ArrowDown":
        focus(flat[Math.min(idx + 1, flat.length - 1)]?.node.id);
        break;
      case "ArrowUp":
        focus(flat[Math.max(idx - 1, 0)]?.node.id);
        break;
      case "Home":
        focus(flat[0]?.node.id);
        break;
      case "End":
        focus(flat[flat.length - 1]?.node.id);
        break;
      case "ArrowRight":
        if (cur.hasChildren && !isOpen) toggle(cur.node.id, true);
        else if (cur.hasChildren) focus(flat[idx + 1]?.node.id);
        break;
      case "ArrowLeft":
        if (cur.hasChildren && isOpen) toggle(cur.node.id, false);
        else focus(cur.parentId);
        break;
      case "Enter":
      case " ":
        activate(cur);
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  };

  const renderLabel = (n: NavNode) => (n.labelKey ? t(n.labelKey) : (n.label ?? ""));

  const renderNodes = (list: NavNode[], level: number, parentId: string | null) =>
    list.map((node, i) => {
      const hasChildren = !!node.children && node.children.length > 0;
      const isOpen = hasChildren && expanded.has(node.id);
      const isActive = node.id === activeId;
      const holdsActive = !isActive && !isOpen && activeAncestors.has(node.id);
      const item: FlatItem = { node, level, parentId, hasChildren };
      const vehicleCount = node.kind === "site" ? countVehicles(node) : 0;
      return (
        <li
          key={node.id}
          ref={(el) => {
            if (el) refs.current.set(node.id, el);
            else refs.current.delete(node.id);
          }}
          role="treeitem"
          aria-level={level}
          aria-setsize={list.length}
          aria-posinset={i + 1}
          aria-expanded={hasChildren ? isOpen : undefined}
          aria-selected={isActive}
          aria-current={isActive ? "page" : undefined}
          tabIndex={node.id === tabbableId ? 0 : -1}
          className={`tree-item kind-${node.kind}`}
          onFocus={(e) => {
            if (e.target === e.currentTarget) setFocusedId(node.id);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setFocusedId(node.id);
            activate(item);
          }}
        >
          <div className={`tree-row${isActive ? " is-active" : ""}${holdsActive ? " holds-active" : ""}`} style={{ ["--level" as string]: level }}>
            {node.kind === "group" ? (
              <>
                <span className="tree-group-label">{renderLabel(node)}</span>
                {holdsActive && (
                  <>
                    <span className="tree-active-dot" aria-hidden="true" />
                    <span className="sr-only">, {t("nav.holdsCurrent")}</span>
                  </>
                )}
                <Icon name={isOpen ? "chevronDown" : "chevronRight"} size={14} className="tree-chevron" />
              </>
            ) : (
              <>
                {hasChildren ? (
                  <span
                    className="tree-toggle"
                    aria-hidden="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFocusedId(node.id);
                      toggle(node.id);
                    }}
                  >
                    <Icon name={isOpen ? "chevronDown" : "chevronRight"} size={14} />
                  </span>
                ) : (
                  <span className="tree-toggle tree-toggle-empty" aria-hidden="true" />
                )}
                {node.icon && <Icon name={node.icon} size={17} className="tree-icon" />}
                {node.kind === "vehicle" && node.status && (
                  <>
                    <span className={`status-dot tone-${node.status}`} aria-hidden="true" />
                    <span className="sr-only">{t(`status.${node.status}`)}: </span>
                  </>
                )}
                <span className={node.kind === "vehicle" ? "tree-label mono" : "tree-label"}>{renderLabel(node)}</span>
                {node.badge ? (
                  <>
                    <span className="tree-badge mono" aria-hidden="true">
                      {node.badge > 99 ? "99+" : node.badge}
                    </span>
                    <span className="sr-only">, {t("topbar.alarmsCount", { count: node.badge })}</span>
                  </>
                ) : null}
                {node.kind === "site" && vehicleCount > 0 && (
                  <span className="tree-count mono" aria-hidden="true">
                    {vehicleCount}
                  </span>
                )}
              </>
            )}
          </div>
          {hasChildren && isOpen && (
            <ul role="group" className="tree-group" style={{ ["--level" as string]: level }}>
              {renderNodes(node.children!, level + 1, node.id)}
            </ul>
          )}
        </li>
      );
    });

  return (
    <ul role="tree" aria-label={t("nav.ariaLabel")} className="tree" onKeyDown={onKeyDown}>
      {renderNodes(nodes, 1, null)}
    </ul>
  );
}
